import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ProviderTypes } from '@guryou/core';
import {
  Block,
  CheckIcon,
  ClockIcon,
  DefaultText,
  FullScreenSpinner,
  LocationIcon,
  MediumText,
  MyBlockIcon,
  openModal,
  SmallGrayText,
} from '@guryou/html-components';
import { createProviderOnboard, stateIsLoading, useAllProviderCategories, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import SelectStaff from 'onboard/components/SelectStaff';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';
import { dealTokens, SystemAutocomplete, SystemButton, SystemFieldError, SystemFormGrid, SystemRadioGroup, SystemTextInput } from 'components/shared';

import CheckboxInfoContainer from '../containers/CheckboxInfoContainer';
import MainContainer from '../containers/MainContainer';

const ERROR_COLOR = dealTokens.color.danger;
const DONE_COLOR = dealTokens.color.success;

const getProviderTextByType = (type, t) => {
  if (type === ProviderTypes.centers) {
    return {
      title: t('ONBOARD.centerTitle'),
      subTitle: t('ONBOARD.centerSubtitle'),
      location: t('ONBOARD.centerLocation'),
      workingHours: t('ONBOARD.centerWorkingHours'),
      staff: t('ONBOARD.centerStaff'),
    };
  }
  return {
    title: t('ONBOARD.profTitle'),
    subTitle: t('ONBOARD.profSubtitle'),
    location: t('ONBOARD.profLocation'),
    workingHours: t('ONBOARD.profWorkingHours'),
    staff: '',
  };
};

const ProviderGeneralInfo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [radio, setRadio] = useState('true');
  const [categoryError, setCategoryError] = useState(null);

  const { state, errors, setErrors, schedules, location, nextActiveStep, setCreatedProviderId, resetState, setActiveStep } = useOnboardContext();

  const allProviderCategories = useAllProviderCategories();
  const providerCreate = useSelector(state => state.providers.createOnboard);
  const auth = useSelector(state => state.auth);

  useEffectOnSuccess(
    state => state.providers.createOnboard,
    data => {
      setCreatedProviderId(data.id);
      nextActiveStep();
    }
  );

  useEffectOnError(
    state => state.providers.createOnboard,
    () => {
      setActiveStep(-1);
    }
  );

  const handleSubmit = data => {
    if (!isLocationSet || !schedules) {
      setErrors({
        providerLocation: !isLocationSet,
        providerSchedules: !schedules,
      });
      return;
    }
    const email = auth.data?.email;
    const type = state.data?.providerType;
    const categoryIds = []
      .concat(data?.categories || [])
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    const rawStaff = data?.staffNames;
    const staff = Array.isArray(rawStaff)
      ? rawStaff.filter(name => typeof name === 'string' && name.trim() !== '')
      : typeof rawStaff === 'string' && rawStaff.trim() !== ''
      ? [rawStaff.trim()]
      : null;

    if (!categoryIds || categoryIds.length < 1) {
      setCategoryError(true);
      return;
    }

    dispatch(
      createProviderOnboard({
        name: data.name.trim(),
        categoryIds: categoryIds,
        staffNames: staff,
        type,
        email,
        schedules,
        ...location,
      })
    );
  };

  const providerType = state.data.providerType;
  const providerText = getProviderTextByType(providerType, t);
  const filteredCategories = allProviderCategories?.data?.filter(x => x.type === providerType);
  const isLocationSet = Object.keys(location).length !== 0;

  if (stateIsLoading(providerCreate)) {
    return <FullScreenSpinner />;
  }

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit}>
        <TitleContainer title={providerText.title} subTitle={providerText.subTitle} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={6}>
          <SystemFormGrid singleColumn>
            <SystemTextInput
              label={providerType === ProviderTypes.centers ? t('ONBOARD.centerName') : t('ONBOARD.professionalName')}
              name="name"
              inputProps={{ maxLength: 100 }}
              required
            />

            <SystemAutocomplete required isMultiSelect name="categories" label={t('ONBOARD.category')} options={filteredCategories} value={null} />
          </SystemFormGrid>
          {categoryError && <SystemFieldError>{t('ONBOARD.categoryRequired')}</SystemFieldError>}

          <Block margin={0} marginTop={5} marginBottom={5}>
            <SmallGrayText marginBottom={2}>{providerText.location}</SmallGrayText>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: dealTokens.space[3] }}>
              <SystemButton
                variant="ghost"
                icon={
                  errors.providerLocation ? (
                    <MyBlockIcon htmlColor={ERROR_COLOR} />
                  ) : isLocationSet ? (
                    <CheckIcon htmlColor={DONE_COLOR} />
                  ) : (
                    <LocationIcon />
                  )
                }
                onClick={() => dispatch(openModal('setLocation'))}
                style={
                  errors.providerLocation
                    ? { borderColor: ERROR_COLOR, color: ERROR_COLOR }
                    : isLocationSet
                    ? { borderColor: DONE_COLOR, color: DONE_COLOR }
                    : undefined
                }
              >
                {t('ONBOARD.location')}
              </SystemButton>

              <SystemButton
                variant="ghost"
                icon={
                  errors.providerSchedules ? (
                    <MyBlockIcon htmlColor={ERROR_COLOR} />
                  ) : schedules ? (
                    <CheckIcon htmlColor={DONE_COLOR} />
                  ) : (
                    <ClockIcon />
                  )
                }
                onClick={() => dispatch(openModal('setWorkingHours'))}
                style={
                  errors.providerSchedules
                    ? { borderColor: ERROR_COLOR, color: ERROR_COLOR }
                    : schedules
                    ? { borderColor: DONE_COLOR, color: DONE_COLOR }
                    : undefined
                }
              >
                {t('ONBOARD.schedule')}
              </SystemButton>
            </div>
          </Block>

          {providerType === ProviderTypes.centers && (
            <Block margin={0} marginTop={5} marginBottom={5}>
              <SystemRadioGroup
                label={providerText.staff}
                name="radio"
                value={radio}
                onChange={setRadio}
                options={[
                  { value: 'true', label: t('ONBOARD.noOnlyMe') },
                  { value: 'false', label: t('ONBOARD.yes') },
                ]}
              />
            </Block>
          )}
          {providerType === ProviderTypes.centers && radio === 'false' && <SelectStaff />}

          <div style={{ display: 'flex', gap: dealTokens.space[3], marginTop: dealTokens.space[5] }}>
            <SystemButton type="button" variant="ghost" onClick={() => resetState()}>
              {t('ONBOARD.prev')}
            </SystemButton>

            <SystemButton type="submit" variant="primary">
              {t('ONBOARD.next')}
            </SystemButton>
          </div>
        </Block>
      </FormContainer>
      <InfoContainer>
        <MediumText lineHeight="1.3">{t('ONBOARD_TEXT.providerTitle')}</MediumText>
        <DefaultText color={dealTokens.color.textMuted} marginTop={2}>
          {t('ONBOARD_TEXT.providerSubtitle')}
        </DefaultText>

        <div style={{ marginTop: dealTokens.space[6], paddingTop: dealTokens.space[5], borderTop: `1px solid ${dealTokens.color.border}` }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: dealTokens.font.weightSemibold,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: dealTokens.color.textMuted,
              marginBottom: dealTokens.space[4],
            }}
          >
            {t('ONBOARD_TEXT.providerCheckboxTitle')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: dealTokens.space[4] }}>
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox1Text')} />
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox2Text')} />
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.providerCheckbox3Text')} />
          </div>
        </div>

        <PlayGuideVideoEmbed videoId={guideVideos.providerGeneralInfo.videoId} modalTitle={guideVideos.providerGeneralInfo.title} />
      </InfoContainer>
    </MainContainer>
  );
};

export default ProviderGeneralInfo;
